import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import Icon from "../components/Icon";
import { company } from "../data/content";

function encodeForm(data) {
  return Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join("&");
}

export default function Contact() {
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    setStatus("submitting");
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encodeForm(payload),
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <>
      <Seo path="/contact" />

      <header className="page-hero">
        <div className="page-hero__bg">
          <img src="/images/port-aerial-crane.jpg" alt="Aerial view of container terminal cranes" />
        </div>
        <div className="container page-hero__inner">
          <span className="page-hero__crumb">
            <Link to="/">Home</Link> / Contact
          </span>
          <h1>Contact Us</h1>
          <p>
            Tell us about your shipment or project. Our team responds with a tailored
            plan built around your timeline and cargo.
          </p>
        </div>
      </header>

      <section className="section">
        <div className="container contact-grid">
          <div>
            <div className="contact-card">
              <div className="contact-card__icon">
                <Icon name="phoneCall" />
              </div>
              <div>
                <h3>Telephone</h3>
                <a href={`tel:${company.phone.replace(/[^+\d]/g, "")}`}>{company.phone}</a>
              </div>
            </div>
            <div className="contact-card">
              <div className="contact-card__icon">
                <Icon name="phoneCall" />
              </div>
              <div>
                <h3>Mobile</h3>
                <a href={`tel:${company.mobile.replace(/[^+\d]/g, "")}`}>{company.mobile}</a>
              </div>
            </div>
            <div className="contact-card">
              <div className="contact-card__icon">
                <Icon name="handshake" />
              </div>
              <div>
                <h3>Partnership &amp; Business</h3>
                <a href={`mailto:${company.emails.partnership}`}>{company.emails.partnership}</a>
              </div>
            </div>
            <div className="contact-card">
              <div className="contact-card__icon">
                <Icon name="users" />
              </div>
              <div>
                <h3>Customer Service</h3>
                <a href={`mailto:${company.emails.customerService}`}>{company.emails.customerService}</a>
              </div>
            </div>
          </div>

          <div className="contact-form">
            {status === "success" ? (
              <div className="form-success">
                <div className="form-success__icon">
                  <Icon name="check" size={26} />
                </div>
                <h3>Message sent</h3>
                <p>
                  Thanks for reaching out — our team will get back to you shortly at the
                  email address you provided.
                </p>
                <button type="button" className="btn btn-ghost" onClick={() => setStatus("idle")}>
                  Send another message
                </button>
              </div>
            ) : (
              <form
                name="contact"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
              >
                <h3>Send an Inquiry</h3>
                <p>Fill in the details below and our partnership team will follow up directly.</p>

                {/* Required for Netlify's static form detection / AJAX submission */}
                <input type="hidden" name="form-name" value="contact" />
                <p className="sr-only" aria-hidden="true">
                  <label>
                    Don&rsquo;t fill this out if you&rsquo;re human: <input name="bot-field" tabIndex="-1" autoComplete="off" />
                  </label>
                </p>

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="name">Full Name</label>
                    <input id="name" name="name" type="text" placeholder="Your name" required />
                  </div>
                  <div className="form-field">
                    <label htmlFor="company">Company</label>
                    <input id="company" name="company" type="text" placeholder="Company name" />
                  </div>
                  <div className="form-field">
                    <label htmlFor="email">Email</label>
                    <input id="email" name="email" type="email" placeholder="you@company.com" required />
                  </div>
                  <div className="form-field">
                    <label htmlFor="phone">Phone</label>
                    <input id="phone" name="phone" type="tel" placeholder="+62 ..." />
                  </div>
                  <div className="form-field form-field--full">
                    <label htmlFor="service">Service Needed</label>
                    <select id="service" name="service" defaultValue="International Freight Forwarding">
                      <option>International Freight Forwarding</option>
                      <option>Customs Clearance</option>
                      <option>Warehousing & Distribution</option>
                      <option>Inland Transportation</option>
                      <option>Project Logistics & Special Cargo</option>
                      <option>Event & Exhibition Logistics</option>
                      <option>Supply Chain Solutions</option>
                    </select>
                  </div>
                  <div className="form-field form-field--full">
                    <label htmlFor="message">Message</label>
                    <textarea id="message" name="message" rows="4" placeholder="Tell us about your shipment or project" required />
                  </div>
                </div>

                {status === "error" && (
                  <p className="form-error">
                    Something went wrong sending your message. Please try again, or email
                    us directly at{" "}
                    <a href={`mailto:${company.emails.partnership}`}>{company.emails.partnership}</a>.
                  </p>
                )}

                <button type="submit" className="btn btn-primary" disabled={status === "submitting"}>
                  {status === "submitting" ? "Sending..." : "Send Inquiry"}{" "}
                  {status !== "submitting" && <Icon name="arrowRight" size={18} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
